import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/handleError";
import {thunkHandlers} from "../../../store/thunkHandlers";
import {AppDispatch, RootState} from "../../../store";
import {IMachineryGroup} from "../../../models/iMachineryGroup";
import {machineryGroupsAPI} from "../api";
import {updateLightListRow} from "../../machinery/model/slice";

type ThunkConfig = {
    dispatch: AppDispatch;
    state: RootState;
    rejectValue: string;
};

/**
 * Локальные патчи строк таблицы техники.
 *
 * Бэкенд рассылает такие же патчи по сокету (комната map_machinery_list), но
 * автор изменения не должен ждать round-trip: колонка «Группа» обязана
 * обновиться сразу после закрытия диалога. Патч идемпотентен — повторное
 * применение из сокета ничего не портит.
 */
const patchRows = (
    dispatch: AppDispatch,
    rows: Array<{id: string; group_id: string | null; group_name: string | null}>,
) => {
    for (const row of rows) dispatch(updateLightListRow(row));
};

export const fetchGetMachineryGroups = createAsyncThunk<IMachineryGroup[], void, ThunkConfig>(
    "machinery_groups/get_all",
    async (_, {rejectWithValue}) => {
        try {
            return await machineryGroupsAPI.getAll();
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchAddMachineryGroup = createAsyncThunk<IMachineryGroup, string, ThunkConfig>(
    "machinery_groups/add",
    async (name, {dispatch, rejectWithValue}) => {
        try {
            const group = await machineryGroupsAPI.add(name);
            thunkHandlers.success("Группа создана", dispatch);
            return group;
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchUpdateMachineryGroup = createAsyncThunk<
    IMachineryGroup,
    {id: string; name: string},
    ThunkConfig
>("machinery_groups/update", async ({id, name}, {dispatch, getState, rejectWithValue}) => {
    try {
        const group = await machineryGroupsAPI.update(id, name);
        // Имя денормализовано в строках таблицы — переименование должно
        // доехать до колонки «Группа», а не только до списка групп.
        patchRows(
            dispatch,
            getState()
                .machinery.light_list.filter((row) => row.group_id === id)
                .map((row) => ({id: row.id, group_id: id, group_name: group.name})),
        );
        return group;
    } catch (e) {
        return rejectWithValue(thunkHandlers.error(e, dispatch));
    }
});

export const fetchDeleteMachineryGroup = createAsyncThunk<string, string, ThunkConfig>(
    "machinery_groups/delete",
    async (id, {dispatch, getState, rejectWithValue}) => {
        try {
            await machineryGroupsAPI.delete(id);
            // Техника не удаляется вместе с группой — она остаётся без группы
            // (на бэке это делает FK ON DELETE SET NULL).
            patchRows(
                dispatch,
                getState()
                    .machinery.light_list.filter((row) => row.group_id === id)
                    .map((row) => ({id: row.id, group_id: null, group_name: null})),
            );
            thunkHandlers.success("Группа удалена", dispatch);
            return id;
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchCreateMachineryGroupShare = createAsyncThunk<IMachineryGroup, string, ThunkConfig>(
    "machinery_groups/create_share",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            const group = await machineryGroupsAPI.createShare(id);
            thunkHandlers.success("Ссылка выпущена", dispatch);
            return group;
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchRevokeMachineryGroupShare = createAsyncThunk<IMachineryGroup, string, ThunkConfig>(
    "machinery_groups/revoke_share",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            const group = await machineryGroupsAPI.revokeShare(id);
            thunkHandlers.success("Доступ по ссылке отозван", dispatch);
            return group;
        } catch (e) {
            return rejectWithValue(thunkHandlers.error(e, dispatch));
        }
    },
);

export const fetchSetMachineryGroupMembers = createAsyncThunk<
    IMachineryGroup,
    {id: string; machinery_ids: string[]},
    ThunkConfig
>("machinery_groups/set_members", async ({id, machinery_ids}, {dispatch, getState, rejectWithValue}) => {
    try {
        const group = await machineryGroupsAPI.setMembers(id, machinery_ids);
        const selected = new Set(machinery_ids);
        patchRows(
            dispatch,
            getState()
                .machinery.light_list.filter(
                    // Изменились только те, кого добавили, и те, кого отцепили.
                    (row) => selected.has(row.id) || row.group_id === id,
                )
                .map((row) =>
                    selected.has(row.id)
                        ? {id: row.id, group_id: id, group_name: group.name}
                        : {id: row.id, group_id: null, group_name: null},
                ),
        );
        // Машина могла прийти из другой группы — там счётчик уже неверен.
        // Пересчитывать его на фронте пришлось бы дублируя логику бэка,
        // поэтому просто перечитываем список групп.
        void dispatch(fetchGetMachineryGroups());
        thunkHandlers.success("Состав группы сохранён", dispatch);
        return group;
    } catch (e) {
        return rejectWithValue(thunkHandlers.error(e, dispatch));
    }
});
